import React from 'react';
import { DELAY_COLORS, classifyOrder, formatHours } from './DelayBadge';

/**
 * Wave 3.78 — Compact horizontal Gantt-style row for a single order.
 * Wave 3.91 —
 *   1. Fulfilling Requirements is now split into per-material sub-segments
 *      (Gold / Diamonds / Colour Stones / Pearls). Only materials that are
 *      *actually applicable to this order* are rendered — a track is
 *      included when the order has either a requirement of that material
 *      OR the procurement track has been opened (backend already filters
 *      unused sub-tracks out of ``stage_history``).
 *   2. Each segment now displays the elapsed time (e.g. "2.2d", "1.5h")
 *      directly on the bar, matching the PDF layout.
 *   3. Blocked segments render with a hatched pattern *without* any
 *      elapsed-time label so they read as "waiting" rather than "active".
 *
 * Layout:
 *   [order meta 220px] [ ---- stage segments ---- ] [ right-side status pill ]
 */

// Short labels used inside the Gantt cells to keep them readable.
// Falls back to the first word of the stage label when a specific mapping
// is not defined here.
const SHORT_LABELS = {
  cad_confirmed:          'CAD',
  cam_confirmed:          'CAM',
  chamka_issued:          'Chamka',
  ghat_confirmed:         'Ghat',
  fulfilling_requirements:'Fulfilling',
  fulfilling_gold:        'Gold',
  fulfilling_diamond:     'Diamonds',
  fulfilling_color_stone: 'C.Stones',
  fulfilling_pearl:       'Pearls',
  setting:                'Setting',
  quality_check:          'Quality',
  delivery_completed:     'Delivery',
};

// Materials that live under fulfilling_requirements. Kept in the canonical
// visual order (Gold → Diamonds → Colour Stones → Pearls).
const FULFILLING_SUB_KINDS = [
  'fulfilling_gold',
  'fulfilling_diamond',
  'fulfilling_color_stone',
  'fulfilling_pearl',
];

function shortLabelFor(kind, fallback) {
  return SHORT_LABELS[kind] || (fallback || '').split(' ')[0];
}

export function GanttRow({ order, stages, sla_template, onClick }) {
  const hist = order.stage_history || [];

  // Wave 3.91 — Expand fulfilling_requirements into whichever material
  // sub-tracks actually apply to this order. Anything the backend didn't
  // include in ``stage_history`` (no requirement + no procurement track)
  // is intentionally omitted.
  const subTracksForOrder = hist.filter(
    (h) => FULFILLING_SUB_KINDS.indexOf(h.kind) !== -1,
  );

  // Build an effective stage list for THIS order's Gantt row. The width
  // of the fulfilling group is preserved and evenly split across whichever
  // sub-tracks the order has (or falls back to a single Fulfilling cell
  // when the order has no sub-tracks at all).
  const slaMap = {};
  (sla_template || []).forEach((s) => {
    slaMap[s.kind] = Number(s.sla_days || 0) * 24;
  });
  const totalScale = Math.max(
    (sla_template || []).reduce((a, s) => a + Number(s.sla_days || 0) * 24, 0) || 1,
    1,
  );

  const effectiveStages = [];
  (stages || []).forEach((s) => {
    if (s.kind === 'fulfilling_requirements' && subTracksForOrder.length > 0) {
      const parentSla = slaMap[s.kind] || 24;
      const perSubSla = parentSla / subTracksForOrder.length;
      subTracksForOrder.forEach((sub) => {
        effectiveStages.push({
          kind: sub.kind,
          label: sub.label,
          _sla_hours: perSubSla,
          _is_sub: true,
        });
      });
    } else {
      effectiveStages.push({
        kind: s.kind,
        label: s.label,
        _sla_hours: slaMap[s.kind] || 24,
        _is_sub: false,
      });
    }
  });

  const dc = DELAY_COLORS;
  const cls = classifyOrder(order);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' && onClick) onClick(e); }}
      className="grid grid-cols-[260px_1fr_140px] gap-3 items-center px-3 py-2.5 rounded-md hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-accent)] cursor-pointer border border-transparent hover:border-[var(--divider)] transition-colors duration-150"
      data-testid={`prod-timeline-gantt-row-${order.order_no}`}
    >
      {/* Left — order meta */}
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-[var(--ink-navy)] font-mono tabular-nums truncate">
          {order.order_no}
        </div>
        {/* Wave 3.99g — Title on its own line so operators can scan
            the piece being manufactured without opening the order. */}
        {order.title && (
          <div className="text-[12px] text-slate-700 truncate" title={order.title}>
            {order.title}
          </div>
        )}
        <div className="text-[11px] text-slate-500 truncate">
          {order.customer_name || '—'} · {order.goldsmith_name || '—'}
        </div>
        <div className="text-[10px] text-slate-400 truncate">
          {order.jewellery_type || '—'}{order.priority && order.priority !== 'normal' ? ` · ${order.priority}` : ''}
        </div>
      </div>

      {/* Center — Gantt segments */}
      <div className="flex items-stretch h-11 rounded-md overflow-hidden border border-[var(--divider)]">
        {effectiveStages.map((meta) => {
          const seg = hist.find((h) => h.kind === meta.kind) || { status: 'not_started', hours: 0 };
          const slaHrs = meta._sla_hours || 24;
          const share = slaHrs / totalScale;
          const percentBase = Math.max(share * 100, 4); // ensure a min width

          // Segment fill logic:
          // - completed:   filled navy
          // - active:      tint based on delay classification of the order
          // - blocked:     dashed neutral (waiting on prerequisite)
          // - not_started: pale rail
          let bg = dc.not_started.bg;
          let textColor = dc.not_started.text;
          let dashedBorder = false;
          if (seg.status === 'completed') {
            bg = dc.on_track.bg;
            textColor = dc.on_track.text;
          } else if (seg.status === 'active') {
            const key = cls === 'on_track' ? 'active' : cls;
            const c = dc[key];
            bg = c.bg;
            textColor = c.text;
          } else if (seg.status === 'blocked') {
            bg = dc.blocked.bg;
            textColor = dc.blocked.text;
            dashedBorder = true;
          }

          // Overflow bar when stage exceeded SLA (visualise "spillover"):
          let overflowPct = 0;
          if (seg.status === 'active' && slaHrs > 0 && seg.hours > slaHrs) {
            overflowPct = Math.min(((seg.hours - slaHrs) / slaHrs) * 100, 200);
          }

          const label = shortLabelFor(meta.kind, meta.label);
          const blockedByLabel = seg.blocked_by_label || '';
          const tooltip = seg.status === 'blocked'
            ? `${meta.label} · Waiting on ${blockedByLabel}`
            : `${meta.label} · ${formatHours(seg.hours)}${slaHrs ? ` / ${formatHours(slaHrs)} SLA` : ''}`;

          // Wave 3.91 — Elapsed-time label. Hidden for blocked/not_started
          // to avoid implying activity when the stage is waiting.
          const showElapsed = (seg.status === 'completed' || seg.status === 'active') && seg.hours > 0;
          const elapsedLabel = showElapsed ? formatHours(seg.hours) : '';

          return (
            <div
              key={meta.kind}
              title={tooltip}
              className="relative flex flex-col items-center justify-center text-[10px] font-medium border-r border-white last:border-r-0 min-w-0 overflow-hidden"
              style={{
                background: dashedBorder
                  ? `repeating-linear-gradient(-45deg, ${bg}, ${bg} 4px, ${dc.blocked.border} 4px, ${dc.blocked.border} 5px)`
                  : bg,
                color: textColor,
                flexBasis: `${percentBase}%`,
                flexGrow: percentBase,
              }}
              data-testid={`gantt-seg-${meta.kind}`}
            >
              <span className="truncate px-1 whitespace-nowrap leading-tight max-w-full">
                {label}
              </span>
              {elapsedLabel && (
                <span
                  className="truncate px-1 whitespace-nowrap leading-tight font-mono tabular-nums text-[9px] max-w-full"
                  style={{ opacity: 0.85 }}
                  data-testid={`gantt-seg-elapsed-${meta.kind}`}
                >
                  {elapsedLabel}
                </span>
              )}
              {overflowPct > 0 && (
                <span
                  className="absolute top-0 bottom-0 right-0 pointer-events-none"
                  style={{
                    background: `repeating-linear-gradient(45deg, ${dc.delayed.text}, ${dc.delayed.text} 3px, ${dc.delayed.bg} 3px, ${dc.delayed.bg} 6px)`,
                    width: `${overflowPct / 3}%`,
                    opacity: 0.45,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Right — status pill */}
      <div className="min-w-0 text-right">
        <div className="text-[12px] font-medium text-[var(--ink-navy)] truncate">
          {order.current_stage_label || '—'}
        </div>
        <div className="text-[11px] font-mono tabular-nums text-slate-500">
          {formatHours(order.current_stage_hours)}
          {order.current_stage_sla_hours ? (
            <span className="text-slate-400"> / {formatHours(order.current_stage_sla_hours)}</span>
          ) : null}
        </div>
        {(order.blocked_stages || []).length > 0 && (
          <div className="text-[10px] text-stone-500 truncate" title={`Blocked: ${(order.blocked_stages || []).map((b) => b.label).join(', ')}`}>
            ⏸ {order.blocked_stages.length} waiting
          </div>
        )}
        {order.requirements_progress && order.requirements_progress.total > 0
          && (order.current_stage_kind === 'fulfilling_requirements'
            || (order.blocked_stages || []).some((b) => b.blocked_by === 'fulfilling_requirements')) && (
          <div
            className="text-[10px] mt-0.5 inline-block font-mono tabular-nums px-1 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800"
            title={`${order.requirements_progress.completed} of ${order.requirements_progress.total} requirements fulfilled`}
          >
            {order.requirements_progress.completed}/{order.requirements_progress.total} reqs
          </div>
        )}
      </div>
    </div>
  );
}
